/**
 * Liest einen `text/event-stream`-Response-Body und liefert das geparste
 * JSON jedes `data:`-Feldes. `EventSource` kann weder POST noch eigene Header
 * senden, deshalb fetch + eigenes Parsen.
 */
export async function* readEventStream<T>(
    response: Response,
): AsyncGenerator<T> {
    if (!response.body) return;

    const reader = response.body
        .pipeThrough(new TextDecoderStream())
        .getReader();

    let buffer = "";

    for (;;) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += value;

        let boundary = findFrameEnd(buffer);

        while (boundary !== undefined) {
            const frame = buffer.slice(0, boundary.index);
            buffer = buffer.slice(boundary.index + boundary.length);

            const data = frameData(frame);

            if (data !== undefined) yield JSON.parse(data) as T;

            boundary = findFrameEnd(buffer);
        }
    }
}

/** Frames enden mit einer Leerzeile — `\n\n` oder `\r\n\r\n`. */
function findFrameEnd(
    buffer: string,
): { index: number; length: number } | undefined {
    const lf = buffer.indexOf("\n\n");
    const crlf = buffer.indexOf("\r\n\r\n");

    if (lf === -1 && crlf === -1) return undefined;

    if (crlf !== -1 && (lf === -1 || crlf < lf)) {
        return { index: crlf, length: 4 };
    }

    return { index: lf, length: 2 };
}

/**
 * Mehrere `data:`-Zeilen eines Frames werden laut Spezifikation mit `\n`
 * verbunden; Kommentarzeilen (`: keep-alive`) und `event:` werden ignoriert.
 */
function frameData(frame: string): string | undefined {
    const lines = frame
        .split(/\r?\n/)
        .filter((line) => line.startsWith("data:"))
        .map((line) => line.slice(5).replace(/^ /, ""));

    return lines.length > 0 ? lines.join("\n") : undefined;
}
